// REACT, REDUX, HELMET //
import React, { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet";

// PAGES & LAYOUTS //
import Header from "../layouts/Header/Header";
import Footer from "../layouts/Footer/Footer";
import HomePage from "../pages/Home/HomePage";
import {
  PoliciesCookiesPage,
  PoliciesPrivacyPage,
  PoliciesTermsPage,
} from "../pages/Policies/PoliciesPage";
import NotFoundPage from "../pages/Exceptions/NotFoundPage";
import Cookie from "../layouts/Cookie/Cookie";

// STATES FROM SLICE //
import { getLayoutHeaderHeight } from "../features/app/layoutsSlice";
import ApiProgramPage from "../pages/Program/ApiProgramPage";
import ApiPrintPage from "../pages/Program/ApiPrintPage";
import {
  ServiceLegacyPage,
  ServiceProgramPage,
  ServiceWealthPage,
} from "../pages/Service/ServicePages";
import StepAlpine2026Page from "../pages/ArticlePage/StepAlpine2026Page";
import AssessmentPage from "../pages/Assessment/AssessmentPage";
import AssessmentResultPage from "../pages/Assessment/AssessmentResultPage";
import SelfCheckPage from "../pages/SelfCheck/SelfCheckPage";
import AdminApp from "../admin/AdminApp";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// The admin panel (/admin/*) has its own layout/chrome/auth and deliberately
// skips the public site's Header/Footer/Cookie banner — this has to live
// inside <BrowserRouter> (not in AppRoutes itself) since useLocation only
// works below the router.
const AppShell = () => {
  const location = useLocation();

  if (location.pathname.startsWith("/admin")) {
    return (
      <Routes>
        <Route path="/admin/*" element={<AdminApp />} />
      </Routes>
    );
  }

  // /print/* is Puppeteer's navigation target for PDF export (nvfo-api's
  // pdf.service.ts) — bare, no Header/Footer/Cookie, same reasoning as /admin.
  if (location.pathname.startsWith("/print")) {
    return (
      <Routes>
        <Route path="/print/program/:slug" element={<ApiPrintPage />} />
      </Routes>
    );
  }

  return (
    <>
      <Header />
      <ScrollToTop />
      <main
        className={`transition-all duration-500 ease-in-out z-[999] bg-white`}
        // className={`${headerHeight && "mt-[" + headerHeight + "px]"} relative transition-all duration-500 ease-in-out z-[999]`}
        style={{
          minHeight: `calc(100vh)`,
          WebkitMinHeight: `calc(100vh)`,
        }}
      >
        <Routes>
          {/* Every program (all countries, including the ones that used to
              be hardcoded here) now lives in the CMS and renders through
              this one API-backed page — see PATTERNS.md's migration notes. */}
          <Route path="/program/residence/:slug" element={<ApiProgramPage />} />
          <Route path="/program/passport/:slug" element={<ApiProgramPage />} />

          <Route
            path="/program"
            element={
              <>
                <Helmet>
                  <title>身份规划 | NVFO</title>
                </Helmet>
                <ServiceProgramPage />
              </>
            }
          />
          <Route
            path="/wealth"
            element={
              <>
                <Helmet>
                  <title>财富规划 | NVFO</title>
                </Helmet>
                <ServiceWealthPage />
              </>
            }
          />
          <Route
            path="/legacy"
            element={
              <>
                <Helmet>
                  <title>传承规划 | NVFO</title>
                </Helmet>
                <ServiceLegacyPage />
              </>
            }
          />
          <Route
            path="/news/step-alpine-conference-2026"
            element={
              <>
                <Helmet>
                  <title>STEP Alpine Conference 2026 | NVFO</title>
                  <meta
                    name="description"
                    content="The New Vision Family Office team was invited to attend the STEP Alpine Conference 2026 in Interlaken, Switzerland."
                  />
                </Helmet>
                <StepAlpine2026Page />
              </>
            }
          />
          <Route
            path="/assessment"
            element={
              <>
                <Helmet>
                  <title>移民方案定制 | NVFO</title>
                  <meta
                    name="description"
                    content="AI 智能匹配测评，根据预算、目标、意向地区与办理时效，为您定制最合适的移民方案。"
                  />
                </Helmet>
                <AssessmentPage />
              </>
            }
          />
          <Route
            path="/assessment/result"
            element={
              <>
                <Helmet>
                  <title>移民方案定制结果 | NVFO</title>
                </Helmet>
                <AssessmentResultPage />
              </>
            }
          />
          <Route
            path="/self-check"
            element={
              <>
                <Helmet>
                  <title>海外身份与全球资产规划自查表 | NVFO</title>
                  <meta
                    name="description"
                    content="10个维度自查您和家庭是否需要海外身份、全球资产配置与财富传承的一站式家族办公室规划。"
                  />
                </Helmet>
                <SelfCheckPage />
              </>
            }
          />
          <Route path="/policies/cookies" element={<PoliciesCookiesPage />} />
          <Route path="/policies/privacy" element={<PoliciesPrivacyPage />} />
          <Route path="/policies/terms" element={<PoliciesTermsPage />} />
          <Route
            path="/"
            element={
              <>
                <Helmet>
                  <title>New Vision Family Office</title>
                  <meta
                    name="description"
                    content="Welcome to New Vision Family Office."
                  />
                  <meta name="description" content="" />
                  <meta name="keywords" content="" />
                </Helmet>
                <HomePage />
              </>
            }
          />
          <Route
            path="*"
            element={
              <>
                <Helmet>
                  <title>404 | NVFO</title>
                  <meta name="description" content="Page not found." />
                </Helmet>
                <NotFoundPage />
              </>
            }
          />
        </Routes>
      </main>
      <Footer />
      <Cookie />
    </>
  );
};

export const AppRoutes = () => {
  // TODO: get user which decides if account page is visible

  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
};
